import { useState } from 'react';

import type { SimulatorChannel } from '@/backendApis';

import { useSendSimulatorMessageMutation } from './channel-simulator.query';

const SESSION_ID_STORAGE_KEY = 'channel-simulator-session-id';
// The same rule as SESSION_ID_PATTERN in the backend's simulator_devices.py
const SESSION_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

export const isSessionId = (value: string) => SESSION_ID_PATTERN.test(value);

const readStoredSessionId = (): string => {
  const storedSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
  if (storedSessionId && isSessionId(storedSessionId)) return storedSessionId;
  const newSessionId = crypto.randomUUID().slice(0, 8);
  localStorage.setItem(SESSION_ID_STORAGE_KEY, newSessionId);
  return newSessionId;
};

export const useSimulatorSessionId = () => {
  const [sessionId, setSessionId] = useState(readStoredSessionId);
  const changeSessionId = (nextSessionId: string) => {
    localStorage.setItem(SESSION_ID_STORAGE_KEY, nextSessionId);
    setSessionId(nextSessionId);
  };
  return [sessionId, changeSessionId] as const;
};

export const useSimulatorComposer = (
  sessionId: string,
  channel: SimulatorChannel
) => {
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const sendMutation = useSendSimulatorMessageMutation(sessionId, channel);

  const canSend =
    !sendMutation.isPending && (text.trim().length > 0 || attachment !== null);

  const send = () => {
    if (!canSend) return;
    const sentText = text;
    const sentAttachment = attachment;
    sendMutation.mutate(
      { text: sentText, attachment: sentAttachment ?? undefined },
      {
        // Text typed while the request ran stays in the composer
        onSuccess: () => {
          setText((current) => (current === sentText ? '' : current));
          setAttachment((current) =>
            current === sentAttachment ? null : current
          );
        },
      }
    );
  };

  return {
    text,
    setText,
    attachment,
    setAttachment,
    canSend,
    send,
    error: sendMutation.error,
  };
};

export type SimulatorComposerState = ReturnType<typeof useSimulatorComposer>;
